import React, { useEffect, useState } from 'react';
import { useLang } from '../contexts/LanguageContext';
import { initAffirm, openAffirmPrequal, AFFIRM_MIN_USD, affirmEnabled } from '../lib/bnpl';

/**
 * "Apply with Affirm" button.
 *
 * - Opens Affirm's prequalification modal when tapped.
 * - Hidden entirely if Affirm.js fails to load (we never want to misrepresent
 *   availability of financing).
 * - Hidden when the amount is below Affirm's $50 minimum.
 * - Does NOT recolor or restyle Affirm's logo — we render it inside a neutral
 *   button chrome and use plain text + the official brand name.
 */
export default function ApplyWithAffirm({ price, size = 'md', className = '', fullWidth = false }) {
  const { lang, t } = useLang();
  const [ready, setReady] = useState(false);
  const priceNum = Number(price) || 0;

  useEffect(() => {
    if (!affirmEnabled()) return undefined;
    let cancelled = false;
    initAffirm()
      .then(() => { if (!cancelled) setReady(true); })
      .catch(() => { if (!cancelled) setReady(false); });
    return () => { cancelled = true; };
  }, []);

  if (!affirmEnabled() || !ready) return null;
  if (priceNum < AFFIRM_MIN_USD) return null;

  const label = t('affirm.apply');

  const onClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const opened = openAffirmPrequal(priceNum);
    if (opened) return;
    // Fallback: scroll the nearest "as low as" element into view and click it
    // so the SDK's own prequal flow is triggered. Preserves the user's goal
    // ("see what Affirm offers for this amount") even if openModal is a no-op.
    const alsNode = typeof document !== 'undefined' && document.querySelector('[data-testid^="affirm-als-"] .affirm-as-low-as');
    if (alsNode && typeof alsNode.scrollIntoView === 'function') {
      alsNode.scrollIntoView({ behavior: 'smooth', block: 'center' });
      try { alsNode.click(); } catch (_) { /* noop */ }
    }
  };

  const sizeClass = size === 'lg' ? 'btn-lg' : (size === 'sm' ? 'btn-sm' : '');
  const widthStyle = fullWidth ? { width: '100%', justifyContent: 'center' } : undefined;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`btn btn-affirm ${sizeClass} ${className}`}
      style={widthStyle}
      data-testid={`apply-affirm-${size}`}
      aria-label={label}
    >
      <span className="affirm-logo-text" aria-hidden="true">affirm</span>
      <span>{label}</span>
    </button>
  );
}
